import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl/dist/maplibre-gl-csp";
import { Protocol } from "pmtiles";
import { createMapStyle } from "./mapStyle";
import { applyLayerVisibility } from "./layerVisibility";
import { createPlayerTooltip } from "./playerTooltip";
import followArrowUrl from "./player-follow-arrow.png";
import mapWorkerUrl from "./mapWorkerUrl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./VectorMapViewer.scss";

let pmtilesProtocolRegistered = false;
let arrayPrototypeSanitized = false;
const DEFAULT_ZOOM = 5.45;

const sanitizeEnumerableArrayPrototypeExtensions = () => {
  if (arrayPrototypeSanitized) return;

  Object.getOwnPropertyNames(Array.prototype).forEach((key) => {
    if (key === "constructor") return;
    const descriptor = Object.getOwnPropertyDescriptor(Array.prototype, key);
    if (!descriptor?.enumerable) return;

    Object.defineProperty(Array.prototype, key, {
      ...descriptor,
      enumerable: false,
    });
  });

  arrayPrototypeSanitized = true;
};

const getInitialView = (game, mapInfo) => {
  if (game === "promods") {
    return {
      center: [11.025842816985914, 48.137793525830745],
      zoom: DEFAULT_ZOOM,
    };
  }
  const bounds = mapInfo?.bounds_wgs84;
  if (!bounds) return { center: [0, 0], zoom: DEFAULT_ZOOM };
  return {
    center: [
      (bounds.west + bounds.east) / 2,
      (bounds.south + bounds.north) / 2,
    ],
    zoom: DEFAULT_ZOOM,
  };
};

const VectorMapViewer = ({
  game,
  mapInfo,
  players,
  activeLayers,
  tooltipStrings,
  followedPlayer,
  initialCamera,
  onMapLoad,
  onPlayerClick,
  onStopFollowing,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const callbacksRef = useRef({});
  const playersRef = useRef(players);
  const followStateRef = useRef({ playerId: null, coordinates: null });
  const activeLayersRef = useRef(activeLayers);
  const tooltipStringsRef = useRef(tooltipStrings);
  const [styleReady, setStyleReady] = useState(false);

  callbacksRef.current = { onMapLoad, onPlayerClick, onStopFollowing };
  playersRef.current = players;
  activeLayersRef.current = activeLayers;
  tooltipStringsRef.current = tooltipStrings;

  useEffect(() => {
    if (mapRef.current?.getLayer("players")) {
      applyLayerVisibility(mapRef.current, activeLayers);
    }
  }, [activeLayers]);

  useEffect(() => {
    if (!containerRef.current || !mapInfo) return undefined;

    // linqjs adds enumerable helpers to Array.prototype; MapLibre treats them
    // as sprite entries unless these extensions are made non-enumerable.
    sanitizeEnumerableArrayPrototypeExtensions();
    maplibregl.setWorkerUrl(mapWorkerUrl);

    if (!pmtilesProtocolRegistered) {
      maplibregl.addProtocol("pmtiles", new Protocol().tile);
      pmtilesProtocolRegistered = true;
    }

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: createMapStyle(game, mapInfo),
      minZoom: 4,
      maxZoom: 14,
      pitch: 45,
      ...getInitialView(game, mapInfo),
      ...initialCamera,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.FullscreenControl(), "top-right");

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => map.resize())
        : null;
    if (resizeObserver) resizeObserver.observe(containerRef.current);

    const onPlayerEnter = (event) => {
      map.getCanvas().style.cursor = "pointer";
      const player = event.features?.[0]?.properties;
      if (!player) return;

      popupRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
      })
        .setLngLat(event.lngLat)
        .setHTML(createPlayerTooltip(player, tooltipStringsRef.current))
        .addTo(map);
    };

    const onPlayerLeave = () => {
      map.getCanvas().style.cursor = "";
      popupRef.current?.remove();
      popupRef.current = null;
    };

    map.on("style.load", () => {
      map.loadImage(followArrowUrl).then((image) => {
        if (mapRef.current === map && !map.hasImage("player-follow-arrow")) {
          map.addImage("player-follow-arrow", image.data);
        }
      }).catch((error) => {
        console.error("Unable to load live map follow arrow:", error);
        map.fire("error", { error });
      });
      setStyleReady(true);
      applyLayerVisibility(map, activeLayersRef.current);
      map.on("mouseenter", "players", onPlayerEnter);
      map.on("mouseleave", "players", onPlayerLeave);
      map.on("click", "players", (event) => {
        const playerId = Number(event.features?.[0]?.properties?.id);
        if (Number.isFinite(playerId)) {
          callbacksRef.current.onPlayerClick?.(playerId);
        }
      });
      map.on("dragstart", () => callbacksRef.current.onStopFollowing?.());

      const source = map.getSource("players");
      if (source && playersRef.current) source.setData(playersRef.current);
      callbacksRef.current.onMapLoad?.(map);
    });

    return () => {
      resizeObserver?.disconnect();
      popupRef.current?.remove();
      map.remove();
      mapRef.current = null;
      followStateRef.current = { playerId: null, coordinates: null };
      setStyleReady(false);
    };
  }, [game, mapInfo, initialCamera]);

  useEffect(() => {
    const source = mapRef.current?.getSource("players");
    if (source) source.setData(players);
  }, [players]);

  useEffect(() => {
    if (followedPlayer == null) {
      followStateRef.current = { playerId: null, coordinates: null };
      return;
    }

    const feature = players?.features?.find(
      (item) => Number(item.properties?.id) === Number(followedPlayer)
    );
    if (!feature || feature.geometry.type !== "Point" || !styleReady) return;

    const coordinates = feature.geometry.coordinates;
    const previousFollow = followStateRef.current;
    const map = mapRef.current;

    if (!map) return;
    const camera = {
      center: coordinates,
      bearing: feature.properties?.mapBearing ?? map.getBearing(),
      speed: 2.8,
      curve: 1.15,
      maxDuration: 900,
      essential: true,
    };

    map.flyTo(
      previousFollow.playerId !== followedPlayer
        ? { ...camera, zoom: 12, pitch: 60 }
        : camera
    );

    followStateRef.current = { playerId: followedPlayer, coordinates };
  }, [followedPlayer, players, styleReady]);

  return <div className="vector-live-map-canvas" ref={containerRef} />;
};

export default VectorMapViewer;
